import { createRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "@/hooks/useSafeTranslation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PlanBadge,
  ProgressBar,
  Button,
} from "@/components/ui";
import { toErrorDisplayMessage, toDisplayString, toSafeReactNode } from "@/lib/utils";
import { useUsage, usageBars } from "@/hooks/useUsage";
import { authenticatedRoute } from "../_authenticated";

function UsagePage() {
  const { t } = useTranslation("common");
  const { data: usage, isLoading, error } = useUsage();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("usage.title"))}</h1>
        <p className="text-sm text-text-muted">
          {toSafeReactNode(t("usage.subtitle"))}
          {usage && (
            <span className="ml-2">
              <PlanBadge planId={usage.planId} />
            </span>
          )}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{toSafeReactNode(t("usage.cardTitle"))}</CardTitle>
          <CardDescription>{toSafeReactNode(t("usage.cardDescription"))}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && (
            <p className="text-sm text-text-muted">{toSafeReactNode(t("common.loading"))}</p>
          )}
          {error && (
            <p className="text-sm text-danger">{toErrorDisplayMessage(error)}</p>
          )}
          {usage && (
            <>
              {/* Count, cap and label are paired by usageBars() so they cannot drift apart here. */}
              {usageBars(usage).map((bar) => (
                <ProgressBar
                  key={bar.period}
                  value={bar.used}
                  max={bar.limit ?? 0}
                  label={toDisplayString(t(bar.labelKey))}
                />
              ))}
              {!usage.canCapture && (
                <>
                  <div className="rounded-lg border border-warning bg-warning/10 p-3 text-sm text-warning">
                    {toSafeReactNode(toErrorDisplayMessage(usage.upgradeCopy) || toDisplayString(t("usage.limitReached")))}
                  </div>
                  <Link to="/billing">
                    <Button variant="outline" size="sm">
                      {toSafeReactNode(t("usage.upgradeForMore"))}
                    </Button>
                  </Link>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const usageRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "usage",
  component: UsagePage,
});
